import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { MessageIcon, RocketIcon } from '~/features/enterprise/landing-pages/icons';
import { getEnterpriseLandingAnalyticsLabels } from '~/features/enterprise/landing-pages/utils/get-enterprise-landing-analytics-labels';
import { HeroLayout } from '~/features/experiments/ab_plp_redesign_2025/components/hero-layout';

export type FooterBannerBackgroundConfig = {
  desktop: { objectPosition: string };
  mobile: { objectPosition: string };
  /** When set, used for the base (mobile) breakpoint only; `src` is used for tablet and desktop. */
  mobileSrc?: string;
  src: string;
  tablet: { objectPosition: string };
};

export type FooterBannerConfig = {
  background: FooterBannerBackgroundConfig;
};

export function FooterBannerSection({ footerBanner, slug }: { footerBanner: FooterBannerConfig; slug: string }) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}`;
  const analyticsLabels = getEnterpriseLandingAnalyticsLabels(slug);
  const demoCtaKeyBySlug: Record<string, 'demoCohorts' | 'demoHumanAi' | 'demoSkills'> = {
    cohorts: 'demoCohorts',
    'human-ai-support': 'demoHumanAi',
  };
  const demoCtaKey = demoCtaKeyBySlug[slug] ?? 'demoSkills';
  const { background } = footerBanner;
  const mobileImageSrc = background.mobileSrc ?? background.src;

  return (
    <HeroLayout
      bgColor='#000'
      minHeight={{ md: '480px' }}
      backgroundImageOverlayGradient={{
        base: 'linear(to-b, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 100%)',
        md: 'linear(to-r, rgb(0, 0, 0) 15%, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0) 70%, rgb(0, 0, 0) 100%)',
      }}
      backgroundImageSlot={
        <>
          <Box display={{ base: 'block', md: 'none' }} inset={0} position='absolute'>
            <Image
              alt=''
              loading='lazy'
              quality={80}
              sizes='100vw'
              src={mobileImageSrc}
              style={{ objectFit: 'cover', objectPosition: background.mobile.objectPosition }}
              fill
            />
          </Box>
          <Box display={{ base: 'none', md: 'block', xl: 'none' }} inset={0} position='absolute'>
            <Image
              alt=''
              loading='lazy'
              quality={80}
              sizes='100vw'
              src={background.src}
              style={{ objectFit: 'cover', objectPosition: background.tablet.objectPosition }}
              fill
            />
          </Box>
          <Box display={{ base: 'none', xl: 'block' }} inset={0} position='absolute'>
            <Image
              alt=''
              loading='lazy'
              quality={80}
              sizes='100vw'
              src={background.src}
              style={{ objectFit: 'cover', objectPosition: background.desktop.objectPosition }}
              fill
            />
          </Box>
        </>
      }
      contentContainerProps={{
        padding: {
          base: '274px 16px 16px',
          md: 4,
        },
      }}
      ctaSlot={
        <>
          <ButtonLink
            linkProps={{ href: '/get-started/enterprise' }}
            buttonProps={{
              leftIcon: <RocketIcon height='20px' width='20px' />,
              flex: { base: 1, md: 'unset' },
              onClick: () =>
                trackEvent({
                  name: 'Enterprise Platform CTA Clicked',
                  type: 'click',
                  label: analyticsLabels.demoCta,
                }),
            }}
          >
            {t(`${tPrefix}.cta.${demoCtaKey}`)}
          </ButtonLink>
          <ButtonLink
            linkProps={{ href: '/get-started/enterprise' }}
            buttonProps={{
              variant: 'outline',
              colorScheme: 'white',
              leftIcon: <MessageIcon height='20px' width='20px' />,
              flex: { base: 1, md: 'unset' },
              onClick: () =>
                trackEvent({
                  name: 'Enterprise Platform CTA Clicked',
                  type: 'click',
                  label: analyticsLabels.footerContact,
                }),
            }}
          >
            {t(`${tPrefix}.cta.contactUs`)}
          </ButtonLink>
        </>
      }
      textContentSlot={
        <Flex flexDirection='column' gap={2} maxWidth={{ base: '253px', md: '638px' }} whiteSpace='pre-wrap'>
          <Heading
            as='h2'
            color='white'
            fontSize={{ base: '2.375rem', md: '3.375rem' }}
            fontWeight='600'
            letterSpacing='0.5px'
            lineHeight='1.1'
          >
            {t(`${tPrefix}.footerBanner.title`)}
          </Heading>
          <Text color='gray.300' fontSize='lg' lineHeight='lg'>
            {t(`${tPrefix}.footerBanner.description`)}
          </Text>
        </Flex>
      }
    />
  );
}
